import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Stack, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import moment from "moment";
import { useSelector } from "react-redux";
import { PrimaryButton } from "components/CustomComponents/DesignSystemButtons";
import { ApprovalDateRangeFilter } from "components/CustomComponents/ApprovalDateRangeFilter";
import { getThisMonthRange } from "components/CustomComponents/dateRangeDefaults";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { useSavedViewSelector } from "components/CustomComponents/useSavedViewSelector";
import { customGetAPI } from "services/utils/CustomAPI";
import { checkEditorPermission } from "services/utils/HelperFunction";
import { showToast } from "services/utils/Status";
import {
  designShell,
  designType,
  HEADER_ACTION_BUTTON_WIDTH,
  paginationFooter,
} from "services/constants/designTokens";
import {
  sharedListPageSurfaceSx,
  sharedStickyCustomFilterBarSx,
  sharedStickyFooterBarSx,
} from "components/DesignSystem/sharedUiPatterns";
import {
  canonicalStatus,
  CORRECTION_HEADERS,
  ATTENDANCE_CORRECTION_ENDPOINT,
  mapCorrectionApiRow,
  toFilterRow,
} from "./attendanceCorrectionHelpers";
import { CorrectionStatusTiles } from "./CorrectionStatusTiles";
import { AttendanceCorrectionTable } from "./AttendanceCorrectionTable";
import { CorrectionApplyPanel } from "../CorrectionShared/CorrectionApplyPanel";
import { ViewAttendanceCorrection } from "./ViewAttendanceCorrection";

// This screen shows 10 rows per page by default (matches the reference design),
// which is finer-grained than the app-wide [25,50,75,100] default.
const rowsPerPageOptions = [10, 25, 50, 100];

export const AttendanceCorrection = ({ subModuleId }) => {
  const { domain, userDetails, userScreenAccess } = useSelector(
    (state) => state.tokenReducer,
  );
  // No access check existed here at all -- a viewer-only user could open
  // and submit the create panel with no gate. Same editor check + "block
  // with a toast" pattern the sibling My Request screens (Leave, Off Day
  // Duty, WorkAdjustment) already use, against this module's real
  // submodule name (verified against a live "My Workspace" payload).
  const checkAccess = checkEditorPermission(
    userScreenAccess?.module_details,
    "My Request",
    "Attendance Correction",
  );

  // Records are loaded from the My Zone attendance-correction endpoint for the
  // logged-in user (My Workspace only).
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [viewData, setViewData] = useState(null);
  const [isViewOpen, setIsViewOpen] = useState(false);

  // Date range filter (mirrors Team Workspace's Approve > Attendance
  // Correction screen, which already has one) -- My Workspace previously had
  // no way to narrow the list by date at all. Defaults to This Month, same
  // as Team Workspace's defaultDateRangeFor, so the two screens agree on
  // what "the default view" shows for the same requests (see BUG_017).
  const [fromDate, setFromDate] = useState(() =>
    moment().startOf("month").format("YYYY-MM-DD"),
  );
  const [toDate, setToDate] = useState(() =>
    moment().endOf("month").format("YYYY-MM-DD"),
  );
  const [dateRangeLabel, setDateRangeLabel] = useState("This Month");
  const handleDateRange = useCallback((from, to, label) => {
    setFromDate(moment(from).format("YYYY-MM-DD"));
    setToDate(moment(to).format("YYYY-MM-DD"));
    setDateRangeLabel(label || "Custom Range");
  }, []);
  const startDate = fromDate || "";
  const endDate = toDate || "";

  const fetchCorrections = useCallback(async () => {
    if (!domain || !userDetails?.id || !startDate || !endDate) return;
    setLoading(true);
    const res = await customGetAPI(
      `${domain}${ATTENDANCE_CORRECTION_ENDPOINT}?user_id=${userDetails.id}&start_date=${startDate}&end_date=${endDate}`,
    );
    if (res?.ok && Array.isArray(res.responseDetails)) {
      setRecords(res.responseDetails.map(mapCorrectionApiRow));
    }
    setLoading(false);
  }, [domain, userDetails?.id, startDate, endDate]);

  useEffect(() => {
    fetchCorrections();
  }, [fetchCorrections]);

  const [statusFilter, setStatusFilter] = useState("");
  const [tableRows, setTableRows] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);

  const counts = useMemo(
    () =>
      records.reduce((acc, r) => {
        const s = canonicalStatus(r.status);
        acc[s] = (acc[s] || 0) + 1;
        return acc;
      }, {}),
    [records],
  );

  // Rows fed to CustomFilter: pre-filtered by the clickable status tiles, then
  // normalized so every column addresses a flat row[key].
  const filterInputRows = useMemo(
    () =>
      records
        .filter((r) => !statusFilter || canonicalStatus(r.status) === statusFilter)
        .map(toFilterRow),
    [records, statusFilter],
  );

  const handleFilteredDataChange = useCallback(
    (filtered = []) => {
      setTableRows(filtered);
      setPage((prev) => (prev * rowsPerPage >= filtered.length ? 0 : prev));
    },
    [rowsPerPage],
  );

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const { visibleHeaders, titleOverride } = useSavedViewSelector({
    domain,
    userId: userDetails?.id,
    subModuleId,
    storageKey: "myzone_attendance_correction_saved_views",
    fullHeaders: CORRECTION_HEADERS,
    screenAccess: true,
    onViewChange: () => setPage(0),
  });

  const reportMetadata = useMemo(
    () => ({
      "User Name":
        userDetails?.first_name ||
        userDetails?.user_name ||
        userDetails?.employee_name ||
        userDetails?.username ||
        "N/A",
      "Employee ID": userDetails?.employee_id || "N/A",
      Type: "Attendance Correction",
      "Report Generated On": moment().format("DD-MM-YYYY HH:mm:ss"),
    }),
    [userDetails],
  );

  const paginatedRows = useMemo(
    () => tableRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [tableRows, page, rowsPerPage],
  );

  // The apply panel persists the request to the backend itself; once it reports
  // success we reload from the server so the list reflects the stored state.
  const handleCreate = useCallback(() => {
    setStatusFilter("");
    setPage(0);
    fetchCorrections();
  }, [fetchCorrections]);

  const openApply = () => {
    if (checkAccess) {
      setIsCreateOpen(true);
    } else {
      showToast(
        "error",
        "Access denied: You are not authorized to apply for attendance correction",
      );
    }
  };

  return (
    <Box sx={{ ...sharedListPageSurfaceSx, minHeight: 0 }}>
      {/* Header */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography sx={designType.pageTitle}>
            Attendance Correction
          </Typography>
          <Typography sx={designType.pageSubtitle}>
            Request correction for missing, incorrect or wrong punch entries.
          </Typography>
        </Box>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.25}
          alignItems={{ xs: "stretch", sm: "center" }}
          sx={{ width: { xs: "100%", sm: "auto" } }}
        >
          <ApprovalDateRangeFilter
            startDate={startDate}
            endDate={endDate}
            dateRangeLabel={dateRangeLabel}
            onChange={handleDateRange}
            includeCurrentDate
            showToday
          />
          {checkAccess ? (
            <PrimaryButton
              startIcon={<AddIcon sx={{ fontSize: 18 }} />}
              sx={{
                minWidth: { xs: 0, sm: HEADER_ACTION_BUTTON_WIDTH },
                width: { xs: "100%", sm: "auto" },
              }}
              onClick={openApply}
            >
              New Request
            </PrimaryButton>
          ) : null}
        </Stack>
      </Stack>

      <CorrectionStatusTiles
        counts={counts}
        total={records.length}
        activeStatus={statusFilter}
        onSelect={(s) => {
          setStatusFilter(s);
          setPage(0);
        }}
      />

      <Box sx={sharedStickyCustomFilterBarSx}>
            {/* Default View · Search · Download · Sort · Filters */}
            <CustomFilter
              title="Attendance Correction"
              titleOverride={titleOverride}
              filterHeaders={visibleHeaders}
              data={filterInputRows}
              filteredData={tableRows}
              onFilteredDataChange={handleFilteredDataChange}
              screenAccess={true}
              showSaveButton={true}
              hideCount={true}
              downloadFormats={["excel", "pdf", "csv"]}
              includeMetadata={true}
              metadata={reportMetadata}
              labeledToolbar={true}
              searchPlaceholder="Search by date, type or reason..."
            />

      </Box>

      <Box sx={{ ...designShell.sectionCard, mt: 1.5, borderRadius: 0, overflow: "hidden" }}>
        <Box sx={{ p: 2, pb: 0 }}>
            <AttendanceCorrectionTable
              headers={visibleHeaders}
              rows={paginatedRows}
              onRowClick={(row) => {
                setViewData(row);
                setIsViewOpen(true);
              }}
              loading={loading}
            />

        </Box>

        <Box sx={sharedStickyFooterBarSx}>
              {paginationFooter({
                page,
                rowsPerPage,
                total: tableRows.length,
                itemLabel: "requests",
                onPageChange: setPage,
                onRowsPerPageChange: handleChangeRowsPerPage,
                rowsPerPageOptions,
              })}
        </Box>
      </Box>

      {isCreateOpen ? (
        <CorrectionApplyPanel
          open={isCreateOpen}
          onClose={() => setIsCreateOpen(false)}
          onCreate={handleCreate}
          title="Attendance Correction Request"
          subtitle="Correct missing, incorrect or wrong punch entries for the selected day."
        />
      ) : null}

      {isViewOpen ? (
        <ViewAttendanceCorrection
          open={isViewOpen}
          selectedData={viewData}
          onClose={() => setIsViewOpen(false)}
          onUpdated={handleCreate}
          checkAccess={checkAccess}
        />
      ) : null}
    </Box>
  );
};
